import { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Paper,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import MentionText from "./MentionText";
import CommentInput from "./CommentInput";

const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
};

const CommentItem = ({ comment, currentUserId, isAdmin, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const isAuthor = comment.author?._id === currentUserId;
  const isDeleted = comment.deleted;

  return (
    <Box sx={{ display: 'flex', gap: 1, py: 1 }}>
      <Avatar src={comment.author?.avatarUrl} sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
        {(comment.author?.name || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {isDeleted ? 'Deleted' : comment.author?.name || comment.author?.email || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(comment.createdAt)}
            {comment.editedAt ? ' (edited)' : ''}
          </Typography>
          {!isDeleted && (
            <Box sx={{ ml: 'auto', display: 'flex' }}>
              {isAuthor && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditing(true)}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {(isAuthor || isAdmin) && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => onDelete(comment)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        {editing ? (
          <CommentInput
            initialValue={comment.body}
            submitLabel="Save"
            autoFocus
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              const ok = await onEdit(comment, body);
              if (ok) setEditing(false);
              return ok;
            }}
          />
        ) : (
          <MentionText body={isDeleted ? '[deleted]' : comment.body} />
        )}
      </Box>
    </Box>
  );
};

const CommentThread = ({
  root,
  replies,
  currentUserId,
  isAdmin,
  onReply,
  onEdit,
  onDelete,
  onToggleResolve,
}) => {
  const [replying, setReplying] = useState(false);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {root.anchor?.type === 'field' && (
          <Chip
            label={root.anchor.fieldName || root.anchor.fieldId}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {root.resolved && (
          <Chip label="Resolved" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={root.resolved ? 'Reopen' : 'Resolve'}>
            <IconButton size="small" onClick={() => onToggleResolve(root)}>
              {root.resolved ? (
                <CheckCircleIcon sx={{ fontSize: 18 }} color="success" />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Reply">
            <IconButton size="small" onClick={() => setReplying((r) => !r)}>
              <ReplyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <CommentItem
        comment={root}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      {replies.length > 0 && (
        <Box sx={{ pl: 4, borderLeft: '2px solid', borderColor: 'divider', ml: 1.5 }}>
          {replies.map((reply) => (
            <CommentItem
              key={reply._id}
              comment={reply}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </Box>
      )}

      {replying && (
        <Box sx={{ pl: 4, ml: 1.5, mt: 1 }}>
          <CommentInput
            placeholder="Reply… use @ to mention"
            submitLabel="Reply"
            autoFocus
            onCancel={() => setReplying(false)}
            onSubmit={async (body) => {
              const ok = await onReply(root, body);
              if (ok) setReplying(false);
              return ok;
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default CommentThread;
